import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ManualTimeEntryModal = ({
  isOpen,
  onClose,
  onSave,
  employees = [],
  loading = false
}) => {
  const [formData, setFormData] = useState({
    employeeCode: '',
    date: new Date().toISOString().split('T')[0], // Today's date
    clockIn: '',
    clockOut: '',
    notes: ''
  });
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [errors, setErrors] = useState({});
  const [employeeSearchResults, setEmployeeSearchResults] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        employeeCode: '',
        date: new Date().toISOString().split('T')[0],
        clockIn: '',
        clockOut: '',
        notes: ''
      });
      setSelectedEmployee(null);
      setErrors({});
      setEmployeeSearchResults([]);
      setShowSuggestions(false);
    }
  }, [isOpen]);

  // Search for employees by code
  useEffect(() => {
    if (formData.employeeCode && formData.employeeCode.length > 0) {
      const filteredEmployees = employees.filter(emp => 
        emp.employee_code?.toLowerCase().includes(formData.employeeCode.toLowerCase()) ||
        emp.name?.toLowerCase().includes(formData.employeeCode.toLowerCase())
      );
      setEmployeeSearchResults(filteredEmployees.slice(0, 5)); // Limit to 5 results
      setShowSuggestions(filteredEmployees.length > 0 && !selectedEmployee);
      
      // Auto-select if exact match
      const exactMatch = employees.find(emp => 
        emp.employee_code?.toLowerCase() === formData.employeeCode.toLowerCase()
      );
      if (exactMatch) {
        setSelectedEmployee(exactMatch);
        setShowSuggestions(false);
      }
    } else {
      setEmployeeSearchResults([]);
      setShowSuggestions(false);
      setSelectedEmployee(null);
    }
  }, [formData.employeeCode, employees, selectedEmployee]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
    
    if (field === 'employeeCode') {
      setSelectedEmployee(null);
    }
  };

  const handleEmployeeSelect = (employee) => {
    setFormData(prev => ({ ...prev, employeeCode: employee.employee_code }));
    setSelectedEmployee(employee);
    setShowSuggestions(false);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.employeeCode.trim()) {
      newErrors.employeeCode = 'Mitarbeiternummer ist erforderlich';
    } else if (!selectedEmployee) {
      newErrors.employeeCode = 'Mitarbeiter nicht gefunden';
    }

    if (!formData.date) {
      newErrors.date = 'Datum ist erforderlich';
    }

    if (!formData.clockIn) {
      newErrors.clockIn = 'Eingangszeit ist erforderlich';
    }

    if (!formData.clockOut) {
      newErrors.clockOut = 'Ausgangszeit ist erforderlich';
    }

    // Validate time format and logic
    if (formData.clockIn && formData.clockOut) {
      const clockInTime = new Date(`2000-01-01T${formData.clockIn}`);
      const clockOutTime = new Date(`2000-01-01T${formData.clockOut}`);
      
      if (clockOutTime <= clockInTime) {
        newErrors.clockOut = 'Ausgangszeit muss nach der Eingangszeit liegen';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const timeEntryData = {
      employee_id: selectedEmployee.id,
      employee: selectedEmployee,
      date: formData.date,
      clockIn: formData.clockIn,
      clockOut: formData.clockOut,
      notes: formData.notes
    };

    try {
      await onSave(timeEntryData);
      onClose();
    } catch (error) {
      console.error('Failed to create time entry:', error);
    }
  };

  const calculateWorkingHours = () => {
    if (formData.clockIn && formData.clockOut) {
      // Apply minimum start time of 8:00 and maximum end time of 17:00
      const [inH, inM] = formData.clockIn.split(':').map(Number);
      const [outH, outM] = formData.clockOut.split(':').map(Number);
      
      // Convert to minutes for easier calculation
      let clockInMinutes = inH * 60 + inM;
      let clockOutMinutes = outH * 60 + outM;
      
      // Apply minimum start time of 8:00 (480 minutes)
      const minStartMinutes = 8 * 60; // 8:00 = 480 minutes
      if (clockInMinutes < minStartMinutes) {
        clockInMinutes = minStartMinutes;
      }
      
      // Apply maximum end time of 17:00 (1020 minutes)
      const maxEndMinutes = 17 * 60; // 17:00 = 1020 minutes
      if (clockOutMinutes > maxEndMinutes) {
        clockOutMinutes = maxEndMinutes;
      }
      
      // Calculate working time in minutes
      const workingMinutes = Math.max(0, clockOutMinutes - clockInMinutes);
      
      // Convert back to hours
      const hours = workingMinutes / 60;
      return hours.toFixed(2);
    }
    return '0.00';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-lg font-semibold text-text-primary">
              Zeiteintrag manuell erstellen
            </h2>
            <p className="text-sm text-text-secondary mt-1">
              Erstellen Sie einen neuen Zeiteintrag durch Eingabe der Mitarbeiternummer
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            iconName="X"
            className="text-text-secondary hover:text-text-primary"
          />
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Employee Code Input with Search */}
          <div className="relative">
            <Input
              label="Mitarbeiternummer"
              type="text"
              value={formData.employeeCode}
              onChange={(e) => handleInputChange('employeeCode', e.target.value)}
              error={errors.employeeCode}
              placeholder="M001 oder Mitarbeitername eingeben..."
              onFocus={() => setShowSuggestions(employeeSearchResults.length > 0)}
            />
            
            {/* Selected Employee Display */}
            {selectedEmployee && (
              <div className="mt-2 p-3 bg-success/10 border border-success/20 rounded-md">
                <div className="flex items-center space-x-3">
                  <Icon name="UserCheck" size={20} className="text-success" />
                  <div>
                    <div className="font-medium text-text-primary">{selectedEmployee.name}</div>
                    <div className="text-sm text-text-secondary">
                      {selectedEmployee.employee_code} • {selectedEmployee.hire_type}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Search Suggestions */}
            {showSuggestions && employeeSearchResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-md shadow-lg z-10 max-h-48 overflow-y-auto">
                {employeeSearchResults.map(employee => (
                  <button
                    key={employee.id}
                    type="button"
                    onClick={() => handleEmployeeSelect(employee)}
                    className="w-full p-3 text-left hover:bg-muted flex items-center space-x-3 border-b border-border last:border-b-0"
                  >
                    <Icon name="User" size={16} className="text-text-secondary" />
                    <div>
                      <div className="font-medium text-text-primary">{employee.name}</div>
                      <div className="text-sm text-text-secondary">
                        {employee.employee_code} • {employee.hire_type}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Date Input */}
          <Input
            label="Datum"
            type="date"
            value={formData.date}
            onChange={(e) => handleInputChange('date', e.target.value)}
            error={errors.date}
          />

          {/* Time Inputs */}
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Eingangszeit"
              type="time"
              value={formData.clockIn}
              onChange={(e) => handleInputChange('clockIn', e.target.value)}
              error={errors.clockIn}
            />
            <Input
              label="Ausgangszeit"
              type="time"
              value={formData.clockOut}
              onChange={(e) => handleInputChange('clockOut', e.target.value)}
              error={errors.clockOut}
            />
          </div>

          {/* Working Hours Display */}
          {formData.clockIn && formData.clockOut && (
            <div className="p-3 bg-primary/10 border border-primary/20 rounded-md">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Clock" size={16} className="text-primary" />
                <span className="text-sm font-medium text-text-primary">
                  Arbeitszeit: {calculateWorkingHours()} Stunden
                </span>
              </div>
              {(() => {
                const [inH, inM] = formData.clockIn.split(':').map(Number);
                const [outH, outM] = formData.clockOut.split(':').map(Number);
                const clockInMinutes = inH * 60 + inM;
                const clockOutMinutes = outH * 60 + outM;
                const minStartMinutes = 8 * 60;
                const maxEndMinutes = 17 * 60;
                
                const showWarnings = clockInMinutes < minStartMinutes || clockOutMinutes > maxEndMinutes;
                
                if (showWarnings) {
                  return (
                    <div className="text-xs text-text-secondary space-y-1">
                      {clockInMinutes < minStartMinutes && (
                        <div>• Arbeitszeit beginnt um 8:00 (Zeit vor 8:00 wird nicht gezählt)</div>
                      )}
                      {clockOutMinutes > maxEndMinutes && (
                        <div>• Arbeitszeit endet um 17:00 (Zeit nach 17:00 wird nicht gezählt)</div>
                      )}
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Notizen (optional)
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Zusätzliche Informationen..."
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Abbrechen
            </Button>
            <Button
              type="submit"
              variant="default"
              loading={loading}
              iconName="Plus"
              iconPosition="left"
            >
              Zeiteintrag erstellen
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ManualTimeEntryModal;
